import { MaterialIcons } from '@expo/vector-icons';
import { ActivityOrderBy } from '@gripp/shared-logic';
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, TouchableOpacity, View } from 'react-native';
import { Text } from 'react-native-paper';
import { Colors } from '../../themes/colors';
import { DropDownView } from '../modals/dropDownView';

export type ActivitySortDropdownProps = {
  selectedSort: ActivityOrderBy;
  onSortChange: (sort: ActivityOrderBy) => void;
};

const sortOptions = [
  { label: 'Last Updated', value: ActivityOrderBy.MostRecentUpdated },
  { label: 'Reminder Date', value: ActivityOrderBy.ReminderDate },
  { label: 'Creation Date', value: ActivityOrderBy.CreationDate },
];

export const ActivitySortDropdown = ({
  selectedSort,
  onSortChange,
}: ActivitySortDropdownProps) => {
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);
  const anchorRef = useRef<View>(null);

  const selectedOption = sortOptions.find(option => option.value === selectedSort);

  const handleSortSelect = (sort: ActivityOrderBy) => {
    onSortChange(sort);
    setIsVisible(false);
  };

  const toggleModal = () => {
    setIsVisible(!isVisible);
  };

  return (
    <View style={styles.container}>
      <TouchableOpacity
        ref={anchorRef}
        style={styles.triggerButton}
        onPress={toggleModal}
        activeOpacity={0.7}
      >
        <Text style={styles.triggerText}>
          {selectedOption?.label || 'Last Updated'}
        </Text>
        <MaterialIcons
          name="arrow-downward"
          size={20}
          color={Colors.role.textStrong}
          style={styles.arrowIcon}
        />
      </TouchableOpacity>

      <DropDownView
        isVisible={isVisible}
        onDismiss={() => setIsVisible(false)}
        anchorRef={anchorRef as React.RefObject<View>}
        width={200}
        height={125}
        position="bottom-right"
      >
        <View style={styles.menuContainer}>
          {sortOptions.map((option, index) => (
            <TouchableOpacity
              key={option.value}
              style={[
                styles.menuItem,
                index === sortOptions.length - 1 && styles.lastMenuItem
              ]}
              onPress={() => handleSortSelect(option.value)}
            >
              <Text style={styles.menuItemText}>
                {option.label}
              </Text>
              <View style={styles.radioButtonContainer}>
                <View style={[
                  styles.radioButton,
                  selectedSort === option.value && styles.radioButtonSelected
                ]}>
                  {selectedSort === option.value && (
                    <View style={styles.radioButtonInner} />
                  )}
                </View>
              </View>
            </TouchableOpacity>
          ))}
        </View>
      </DropDownView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    zIndex: 1000,
    elevation: 1000, // For Android
  },
  triggerButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    paddingHorizontal: 0,
    paddingVertical: 0,
    backgroundColor: 'transparent',
  },
  triggerText: {
    fontSize: 14,
    fontWeight: '500',
    color: Colors.role.textStrong,
    marginRight: 4,
  },
  arrowIcon: {
    marginLeft: 0,
    marginTop: 1, // Slight adjustment to align with text baseline
  },
  menuContainer: {
    paddingTop: 0, // Remove top padding completely
    paddingBottom: 0, // Remove bottom margin
  },
  menuItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
    backgroundColor: Colors.role.surface,
    borderBottomWidth: 1,
    borderBottomColor: Colors.role.borderSubtle,
  },
  lastMenuItem: {
    borderBottomWidth: 0, // Remove border from last item
  },
  menuItemText: {
    fontSize: 14,
    fontWeight: '400',
    color: Colors.role.textStrong,
    flex: 1,
  },
  radioButtonContainer: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  radioButton: {
    width: 16,
    height: 16,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: Colors.role.borderEmphasis,
    alignItems: 'center',
    justifyContent: 'center',
  },
  radioButtonSelected: {
    borderColor: Colors.role.surfaceInverse,
  },
  radioButtonInner: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: Colors.role.surfaceInverse,
  },
});
